import { createClient } from "@/app/utils/supabase/clients";
import { cacheServiceHeaders, timeSeriesUrl } from "@/lib/cache/seriesCacheMappers";
import type { TrendSeries, TrendVariant } from "../types";
import type { TrendSeriesScope } from "./trendSeriesScope";

export interface TrendSeriesRequest {
  displayId: string;
  variant: TrendVariant;
}

/** Our own route for every viewer on a shared link; see app/api/trends/series. */
export const sharedTimeSeriesUrl = ({ displayId, variant }: TrendSeriesRequest, { slug }: TrendSeriesScope) =>
  `/api/trends/series?${new URLSearchParams({ slug, displayId, variant })}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const stringOr = (value: unknown, fallback: string) => (typeof value === "string" && value ? value : fallback);

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** How many band values a gauge reads per observation (`ranges_level_series`). */
const BAND_VALUES = 9;

/** A per-date list from the payload, when it has one entry per date. */
const alignedList = (value: unknown, length: number): unknown[] | null =>
  Array.isArray(value) && value.length === length ? value : null;

/**
 * Checks a /ts response and returns it as a `TrendSeries`. Missing labels fall
 * back to the request; anything that isn't a finite number becomes a gap.
 * Throws when `dates` and `values` are missing or don't line up.
 *
 * Each date's 10-year average (`10y_avg`) and band values (`10y_percentiles`)
 * come along when the payload has one per date; a date whose average or 9
 * band values aren't all numbers has none. So does each date's place on its
 * gauge (`gauge_percentile`); a date without a number there has none.
 */
export function parseTrendSeries(json: unknown, request: TrendSeriesRequest): TrendSeries {
  if (!isRecord(json)) throw new Error(`Unexpected response for ${request.displayId}`);
  const { dates, values } = json;
  if (
    !Array.isArray(dates) ||
    !Array.isArray(values) ||
    dates.length !== values.length ||
    !dates.every((d) => typeof d === "string")
  ) {
    throw new Error(`Malformed series for ${request.displayId}: dates and values don't line up`);
  }

  const avgs = alignedList(json["10y_avg"], dates.length);
  const bandLists = alignedList(json["10y_percentiles"], dates.length);
  const hasBands = !!avgs && !!bandLists;
  const gaugePercentiles = alignedList(json.gauge_percentile, dates.length);

  const rows = (dates as string[]).map((date, i) => {
    const v = values[i];
    const avg = avgs?.[i];
    const bands = bandLists?.[i];
    const gaugePercentile = gaugePercentiles?.[i];
    return {
      date,
      value: isFiniteNumber(v) ? v : null,
      avg10y: isFiniteNumber(avg) ? avg : null,
      bands: Array.isArray(bands) && bands.length === BAND_VALUES && bands.every(isFiniteNumber) ? bands : null,
      gaugePercentile: isFiniteNumber(gaugePercentile) ? gaugePercentile : null,
    };
  });
  // Readouts binary-search the dates, so make sure they ascend.
  rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  return {
    display_id: stringOr(json.display_id, request.displayId),
    display_name: stringOr(json.display_name, request.displayId),
    display_units: stringOr(json.display_units, ""),
    variant_name: stringOr(json.variant_name, request.variant),
    frequency: stringOr(json.frequency, ""),
    dates: rows.map((r) => r.date),
    values: rows.map((r) => r.value),
    ...(hasBands ? { avg10y: rows.map((r) => r.avg10y), bands: rows.map((r) => r.bands) } : {}),
    ...(gaugePercentiles ? { gaugePercentile: rows.map((r) => r.gaugePercentile) } : {}),
  };
}

/**
 * Full history of one series in one variant. On a shared link (`scope`) every
 * viewer, signed in or not, loads through the link at its access level, as
 * the shared dashboard does. In the app, the viewer loads on their own token,
 * so their roles decide what they see.
 */
export async function fetchTrendSeries(
  request: TrendSeriesRequest,
  scope: TrendSeriesScope | null = null,
): Promise<TrendSeries> {
  if (scope) return readTrendSeries(await fetch(sharedTimeSeriesUrl(request, scope)), request);

  const {
    data: { session },
  } = await createClient().auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Sign in to load trend data");
  return readTrendSeries(await fetch(timeSeriesUrl(request), { headers: cacheServiceHeaders(token) }), request);
}

async function readTrendSeries(response: Response, request: TrendSeriesRequest): Promise<TrendSeries> {
  if (!response.ok) {
    const details = await response.text().catch(() => "");
    throw new Error(details || `Failed to load ${request.displayId} (${response.status})`);
  }
  return parseTrendSeries(await response.json(), request);
}
